/**
 * What arriving by link should do, given what is known at that instant.
 *
 * ⚠ THE LINK LANDED ON A PAGE THAT DID NOT KNOW WHICH CLIENT IT WAS FOR.
 * useTenantStore has no persist and no default: a fresh session reads null
 * until ProtectedRoute reconciles a selection. The old arrival effect fired on
 * the FIRST render, when tenantId was still "", judged "no mismatch" because
 * an empty string is not a different client, spent its one shot, and never ran
 * again. By the time the real client arrived the decision was already made —
 * so the draft opened late, or not at all if the viewer happened to have
 * another client selected.
 *
 * That is the guard-evaluated-before-the-facts-exist shape, the same one that
 * let a stale dossier be judged fresh.
 *
 * ⚠ AND CARRYING THE TENANT IS NOT THE SAME AS SELECTING IT. The link named
 * the client only to WARN about a mismatch, which helps exactly the people who
 * already had the right client open. The reviewer the link exists for — comms,
 * an account lead — is the one who does not.
 *
 * Split out from the component so it can be tested where the decision is made,
 * rather than through a reimplementation of it.
 */
export type ArrivalDecision = 'wait' | 'open' | 'switch' | 'no-access';

export function decideArrival(input: {
  draftId: string | null;
  linkTenant: string | null;
  tenantId: string;
  workspacesLoaded: boolean;
  linkTenantIsMine: boolean;
}): ArrivalDecision {
  const { draftId, linkTenant, tenantId, workspacesLoaded, linkTenantIsMine } = input;
  if (!draftId) return 'wait';
  // A link with no tenant is the old shape; open it under whatever is
  // selected, once something IS selected.
  if (!linkTenant) return tenantId ? 'open' : 'wait';
  if (linkTenant === tenantId) return 'open';
  // ⚠ NOTHING IS DECIDED UNTIL THE CLIENT LIST EXISTS. Before it loads,
  // "not my client" and "not loaded yet" are the same empty array, and
  // treating them alike is how a legitimate link gets refused.
  if (!workspacesLoaded) return 'wait';
  // ⚠ FAILS CLOSED. selectTenantByWebsite falls back to an unlinked selection
  // for a website it cannot find, so switching blindly would put a client the
  // viewer has no membership in into their picker. The server still scopes the
  // read, so nothing leaks, but the screen would claim access that does not
  // exist. Not mine -> say so.
  return linkTenantIsMine ? 'switch' : 'no-access';
}
